import React, { useState } from "react";
import { MetricKey, WindowInfo } from "../api/types";
import { INCLUSION_RULES, METRIC_DEFINITIONS } from "../shared/metricDefinitions";

interface MethodologyPanelProps {
  metrics: MetricKey[];
  window?: WindowInfo;
  defaultOpen?: boolean;
}

// Collapsible "How these numbers are calculated" panel. Lists the formula + plain-English
// explanation for exactly the metrics this report shows, plus the report-wide inclusion rules.
// Truthful to the engine — see shared/metricDefinitions.ts for the code citations.
export default function MethodologyPanel({
  metrics,
  window,
  defaultOpen = false,
}: MethodologyPanelProps) {
  const [open, setOpen] = useState(defaultOpen);

  return (
    <div className="mb-3 rounded-md border border-gray-200 bg-white">
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        aria-expanded={open}
        className="flex w-full items-center justify-between px-4 py-2 text-left text-sm font-semibold text-gray-700 hover:bg-gray-50"
      >
        <span>How these numbers are calculated</span>
        <span className="text-xs text-gray-400">{open ? "▲ hide" : "▼ show"}</span>
      </button>

      {open && (
        <div className="border-t border-gray-100 px-4 py-3 text-sm text-gray-600">
          {window && (
            <p className="mb-3 text-xs text-gray-500">
              Window {window.from} → {window.to} · {window.dayBoundary}
            </p>
          )}

          <dl className="space-y-2">
            {metrics
              .filter((m) => METRIC_DEFINITIONS[m])
              .map((m) => {
                const d = METRIC_DEFINITIONS[m];
                return (
                  <div key={m}>
                    <dt className="font-semibold text-gray-800">{d.label}</dt>
                    <dd>
                      <code className="text-xs text-gray-700">{d.formula}</code>
                      <div className="mt-0.5 text-gray-600">{d.explanation}</div>
                    </dd>
                  </div>
                );
              })}
          </dl>

          <div className="mt-4 border-t border-gray-100 pt-3">
            <div className="mb-1 font-semibold text-gray-800">Inclusion rules</div>
            <ul className="list-disc space-y-1 pl-5">
              {INCLUSION_RULES.map((r) => (
                <li key={r.title}>
                  <span className="font-medium text-gray-800">{r.title}.</span> {r.body}
                </li>
              ))}
            </ul>
          </div>
        </div>
      )}
    </div>
  );
}
